import React from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

// Helper to identify total rows
export const isTotalRow = (node) => {
  if (!node) return false;
  const title = String(node?.title || '').toLowerCase().trim();
  const group = String(node?.group || '').toLowerCase().trim();
  
  return (
    title === 'total assets' ||
    title === 'total liabilities' ||
    title === 'total equity' ||
    title === 'total liabilities + equity' ||
    title === 'total liabilities and equity' ||
    group === 'totalassets' ||
    group === 'totalliabilities' ||
    group === 'totalequity' ||
    group === 'totalliabilitiesandequity'
  );
};

// Helper to get total type for styling
export const getTotalType = (node) => {
  if (!node) return null;
  const title = String(node?.title || '').toLowerCase().trim();
  const group = String(node?.group || '').toLowerCase().trim();
  
  if (title === 'total assets' || group === 'totalassets') return 'totalAssets';
  if (title === 'total liabilities' || group === 'totalliabilities') return 'totalLiabilities';
  if (title === 'total equity' || group === 'totalequity') return 'totalEquity';
  if (title === 'total liabilities + equity' || title === 'total liabilities and equity' || group === 'totalliabilitiesandequity') return 'totalLiabilitiesAndEquity';
  
  return null;
};

// Helper to render a total row with special styling
export const renderTotalRow = (node, columns, sectionType, valuesType, formatMoney, getCellNumber, getValueTextClass) => {
  const isQuarterView = valuesType === "quarters";
  const isTotalOnlyView = valuesType === "total";

  const descColTemplate = isQuarterView
    ? "560px"
    : isTotalOnlyView
      ? "minmax(320px, 1fr)"
      : "minmax(320px, 2fr)";

  const valueColTemplate = isQuarterView ? "minmax(140px, 1fr)" : "140px";
  const totalColTemplate = isQuarterView ? "180px" : "140px";
  const nonTotalColumns = (Array.isArray(columns) ? columns : []).filter((c) => c?.key !== "TOTAL");
  const hasTotal = (Array.isArray(columns) ? columns : []).some((c) => c?.key === "TOTAL");
  const middleCols = nonTotalColumns.length > 0 ? `repeat(${nonTotalColumns.length}, ${valueColTemplate})` : "";
  const gridTemplateColumns = `${descColTemplate} ${middleCols} ${hasTotal ? totalColTemplate : ""}`.trim();
  const shouldStickyTotal = valuesType !== "total" && nonTotalColumns.length > 0;
  const shouldStickyLeft = valuesType !== "total";

  // Determine styling based on the type of total
  const totalType = getTotalType(node);
  
  // Get display name based on total type
  const getDisplayName = () => {
    if (totalType === 'totalAssets') return 'Total Assets';
    if (totalType === 'totalLiabilities') return 'Total Liabilities';
    if (totalType === 'totalEquity') return 'Total Equity';
    if (totalType === 'totalLiabilitiesAndEquity') return 'Total Liabilities + Equity';
    // Fallback to node title or section-based default
    if (node?.title) return node.title;
    return sectionType === 'assets' ? 'Total Assets' : 'Total Liabilities + Equity';
  };
  
  const displayName = getDisplayName();
  
  let rowBgClass, accentTextClass, borderClass;
  
  if (totalType === 'totalAssets') {
    rowBgClass = "bg-green-100";
    accentTextClass = "text-green-800";
    borderClass = "rounded-lg";
  } else if (totalType === 'totalLiabilities') {
    rowBgClass = "bg-red-100";
    accentTextClass = "text-red-800";
    borderClass = "rounded-lg";
  } else if (totalType === 'totalEquity') {
    rowBgClass = "bg-blue-100";
    accentTextClass = "text-blue-800";
    borderClass = "rounded-lg";
  } else if (totalType === 'totalLiabilitiesAndEquity') {
    rowBgClass = "bg-gray-100";
    accentTextClass = "text-gray-800";
    borderClass = "rounded-lg";
  } else {
    // Default based on section type
    rowBgClass = sectionType === 'assets' ? "bg-green-100" : "bg-red-100";
    accentTextClass = sectionType === 'assets' ? "text-green-800" : "text-red-800";
    borderClass = "rounded-lg";
  }

  return (
    <div key={node.id || `total-${displayName}`} className={`${borderClass} rounded`}>
      <div
        className={[
          "grid items-center gap-0 transition-colors duration-200 ease-in-out",
          rowBgClass,
        ].join(" ")}
        style={{ gridTemplateColumns }}
      >
        <div
          className={[
            "py-4 px-3 text-sm font-bold relative",
            shouldStickyLeft ? "sticky left-0 z-50" : "",
            rowBgClass,
            accentTextClass,
          ].join(" ")}
        >
          {shouldStickyLeft ? <div className="absolute top-0 right-0 h-full w-px bg-gray-300" /> : null}
          <div className="relative">{displayName}</div>
        </div>

        {nonTotalColumns.map((c, idx) => {
          const value = getCellNumber(node, c.key, columns);
          const textClass = getValueTextClass ? getValueTextClass(value, accentTextClass) : accentTextClass;
          const isFirstInGroup = c?.key?.startsWith('CUR:');
          const isNotFirstGroup = idx > 0 && isFirstInGroup;
          return (
            <div
              key={`${node.id}:${c.key}`}
              className={[
                "py-4 px-3 text-sm text-right tabular-nums font-bold",
                textClass,
                isNotFirstGroup ? "border-l-2 border-gray-300" : ""
              ].join(" ")}
            >
              {formatMoney(value)}
            </div>
          );
        })}

        {hasTotal ? (
          <div
            className={[
              "py-4 px-3 text-sm text-right tabular-nums font-bold relative",
              shouldStickyTotal ? "sticky right-0 z-50" : "",
              getValueTextClass ? getValueTextClass(getCellNumber(node, "TOTAL", columns), accentTextClass) : accentTextClass,
              rowBgClass,
            ].join(" ")}
          >
            {shouldStickyTotal ? <div className="absolute top-0 left-0 h-full w-px bg-gray-300" /> : null}
            <div className="relative">{formatMoney(getCellNumber(node, "TOTAL", columns))}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

// Helper to render a node (row) in the balance sheet
export const renderNode = (node, level, columns, sectionType, valuesType, expanded, toggleExpanded, formatMoney, getValueTextClass, getCellNumber) => {
  const indent = `${level * 16}px`;
  const isSection = node?.type === "Section";
  const hasChildren = Array.isArray(node?.childrens) && node.childrens.length > 0;
  const isOpen = !!expanded?.[node.id];
  const isQuarterView = valuesType === "quarters";
  const isMonthView = valuesType === "months";
  const isTotalOnlyView = valuesType === "total";

  const descColTemplate = isQuarterView
    ? "560px"
    : isTotalOnlyView
      ? "minmax(320px, 1fr)"
      : "minmax(320px, 2fr)";

  const valueColTemplate = isQuarterView ? "minmax(140px, 1fr)" : "140px";
  const totalColTemplate = isQuarterView ? "180px" : "140px";
  const nonTotalColumns = (Array.isArray(columns) ? columns : []).filter((c) => c?.key !== "TOTAL");
  const hasTotal = (Array.isArray(columns) ? columns : []).some((c) => c?.key === "TOTAL");
  const middleCols = nonTotalColumns.length > 0 ? `repeat(${nonTotalColumns.length}, ${valueColTemplate})` : "";
  const gridTemplateColumns = `${descColTemplate} ${middleCols} ${hasTotal ? totalColTemplate : ""}`.trim();
  const shouldStickyTotal = valuesType !== "total" && nonTotalColumns.length > 0;
  const shouldStickyLeft = valuesType !== "total";

  const formatTopAccordionTitle = (title) => {
    const s = String(title || "").trim();
    if (!s) return s;
    return s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  };

  // Special styling for balance sheet top-level accordions
  const isTopLevel = level === 0;
  const isTopLevelNoChildren = isTopLevel && !hasChildren;
  
  // Determine colors based on section type
  let rowBgClass, accentTextClass, hoverRowClass, hoverCellClass;
  
  if (isTopLevelNoChildren) {
    rowBgClass = sectionType === 'assets' ? "bg-green-100" : sectionType === 'liabilitiesAndEquity' ? "bg-red-100" : "bg-emerald-100";
    accentTextClass = sectionType === 'assets' ? "text-green-800" : sectionType === 'liabilitiesAndEquity' ? "text-red-800" : "text-emerald-800";
    hoverRowClass = sectionType === 'assets' ? "hover:bg-green-100" : sectionType === 'liabilitiesAndEquity' ? "hover:bg-red-100" : "hover:bg-emerald-100";
    hoverCellClass = sectionType === 'assets' ? "group-hover:bg-green-100" : sectionType === 'liabilitiesAndEquity' ? "group-hover:bg-red-100" : "group-hover:bg-emerald-100";
  } else if (isTopLevel && hasChildren) {
    // Top-level accordion with children
    rowBgClass = sectionType === 'assets' ? "bg-green-50" : sectionType === 'liabilitiesAndEquity' ? "bg-red-50" : "bg-gray-50";
    accentTextClass = sectionType === 'assets' ? "text-green-700" : sectionType === 'liabilitiesAndEquity' ? "text-red-700" : "";
    hoverRowClass = sectionType === 'assets' ? "hover:bg-green-100" : sectionType === 'liabilitiesAndEquity' ? "hover:bg-red-100" : "hover:bg-gray-100";
    hoverCellClass = sectionType === 'assets' ? "group-hover:bg-green-100" : sectionType === 'liabilitiesAndEquity' ? "group-hover:bg-red-100" : "group-hover:bg-gray-100";
  } else {
    rowBgClass = isSection ? "bg-gray-50" : "bg-white";
    accentTextClass = "";
    hoverRowClass = "hover:bg-gray-100";
    hoverCellClass = "group-hover:bg-gray-100";
  }

  const displayTitle =
    isSection && level === 0 && hasChildren
      ? formatTopAccordionTitle(node.title)
      : node.title;

  return (
    <div key={node.id}>
      <div
        className={[
          "grid items-center gap-0 transition-colors duration-200 ease-in-out border-b border-gray-100 group",
          rowBgClass,
          hoverRowClass,
          hasChildren ? "cursor-pointer" : "",
        ].join(" ")}
        style={{ gridTemplateColumns }}
        onClick={hasChildren ? () => toggleExpanded(node.id) : undefined}
        role={hasChildren ? "button" : undefined}
      >
        <div
          className={[
            "py-3 px-3 text-sm text-gray-700 relative transition-colors duration-200",
            shouldStickyLeft ? "sticky left-0 z-10" : "",
            rowBgClass,
            hoverCellClass,
          ].join(" ")}
          style={{ paddingLeft: indent }}
        >
          {shouldStickyLeft ? <div className="absolute top-0 right-0 h-full w-px bg-gray-200" /> : null}
          <div className="flex items-center gap-2 px-4 relative">
            {hasChildren ? (
              <ChevronDownIcon
                className={[
                  "h-4 w-4 transition-transform duration-200",
                  accentTextClass || "text-gray-500",
                  isOpen ? "rotate-0" : "-rotate-90",
                ].join(" ")}
              />
            ) : (
              <div className="w-2" />
            )}
            <span className={[isSection ? "font-semibold" : "", accentTextClass].join(" ").trim()}>
              {displayTitle}
            </span>
          </div>
        </div>

        {nonTotalColumns.map((c, idx) => {
          const value = getCellNumber(node, c.key, columns);
          const isFirstInGroup = c?.key?.startsWith('CUR:');
          const isNotFirstGroup = idx > 0 && isFirstInGroup;
          return (
            <div
              key={`${node.id}:${c.key}`}
              className={[
                "py-3 px-3 text-sm text-right tabular-nums font-bold",
                getValueTextClass(value, accentTextClass),
                isNotFirstGroup ? "border-l-2 border-gray-300" : ""
              ].join(" ")}
            >
              {formatMoney(value)}
            </div>
          );
        })}

        {hasTotal ? (
          <div
            key={`${node.id}:TOTAL`}
            className={[
              "py-3 px-3 text-sm text-right tabular-nums font-bold relative transition-colors duration-200",
              shouldStickyTotal ? "sticky right-0 z-10" : "",
              getValueTextClass(getCellNumber(node, "TOTAL", columns), accentTextClass),
              rowBgClass,
              hoverCellClass,
            ].join(" ")}
          >
            {shouldStickyTotal ? <div className="absolute top-0 left-0 h-full w-px bg-gray-200" /> : null}
            <div className="relative">{formatMoney(getCellNumber(node, "TOTAL", columns))}</div>
          </div>
        ) : null}
      </div>

      {hasChildren ? (
        <div
          className={[
            "transition-all duration-200 ease-in-out",
            isOpen ? "max-h-[5000px] opacity-100 overflow-visible" : "max-h-0 opacity-0 overflow-hidden pointer-events-none",
          ].join(" ")}
        >
          {(node.childrens || []).map((child) => renderNode(child, level + 1, columns, sectionType, valuesType, expanded, toggleExpanded, formatMoney, getValueTextClass, getCellNumber))}
        </div>
      ) : null}
    </div>
  );
};
